import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {Body, Caption} from "@khanacademy/wonder-blocks-typography";
import Table, {Column} from "@khan/component-table";
import {logUnreachableCode} from "@khan/logging";
import {getMasteryDisplay} from "@khan/mastery";

import type {
    ActivitySessionsV2Query_user_activityLogV2_activitySessions_sessions as ActivitySessionsReportRow,
    ActivitySessionsV2Query_user_activityLogV2_activitySessions_sessions_MasteryActivitySession_skillLevels as SkillLevelChange,
} from "./__graphql-types__/ActivitySessionsV2Query";
import {SingleSkillLevelChangeIcon} from "./skill-level-change-icon.tsx";

const MultipleSkillChangesModal = ({
    activitySessionsReportRow,
}: {
    activitySessionsReportRow: ActivitySessionsReportRow;
}): React.ReactElement | null => {
    const href =
        "https://support.khanacademy.org/hc/articles/360032987591-How-can-I-understand-my-progress-in-Khan-Academy";

    const skillChangesForRowType = () => {
        switch (activitySessionsReportRow.__typename) {
            case "MasteryActivitySession":
                return activitySessionsReportRow.skillLevels || [];
            default:
                return [];
        }
    };

    return (
        <OnePaneDialog
            title={activitySessionsReportRow.title || ""}
            content={
                <View style={{gap: sizing.size_240}}>
                    <Table data={skillChangesForRowType()}>
                        <Column id="skill" label={t`Skill`}>
                            {(data: SkillLevelChange) => (
                                <span>{data.exercise?.translatedTitle}</span>
                            )}
                        </Column>
                        <Column id="change" align="center" label={t`Change`}>
                            {(data: SkillLevelChange) => (
                                <SingleSkillLevelChangeIcon
                                    skillLevelChange={data}
                                />
                            )}
                        </Column>
                        <Column id="level" label={t`Level`}>
                            {(data: SkillLevelChange) => {
                                // Most changes get mapped to a text label.
                                if (data.__typename === "SkillLevelChange") {
                                    return (
                                        <Body>
                                            {getMasteryDisplay(
                                                data.after || "",
                                            )}
                                        </Body>
                                    );
                                }
                                logUnreachableCode(
                                    `Unknown Skill level type: ${data.__typename}`,
                                    data.__typename,
                                );
                                return null;
                            }}
                        </Column>
                    </Table>
                    <Caption>
                        <Trans>
                            Seeing more skill changes than questions answered?{" "}
                            <Link href={href}>
                                {t`Learn about covering prerequisites.`}
                            </Link>
                        </Trans>
                    </Caption>
                </View>
            }
        />
    );
};

export default MultipleSkillChangesModal;
